import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, Download, Loader2, Package, RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { finishedGoodsInventoryApiService, FinishedGoodsInventoryExtendedSummary } from '../services/finishedGoodsInventoryApi';
import { useBranch } from '../contexts/BranchContext';

const FinishedGoodsInventoryReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<FinishedGoodsInventoryExtendedSummary[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchReport = async () => {
    try {
      setLoading(true);
      // Use the same extended view API as the Finished Goods dashboard, scoped to selected branch
      const branchId = selectedBranch || undefined;
      const response = await finishedGoodsInventoryApiService.getFinishedGoodsInventoryExtendedView(branchId);
      if (response.success && response.data) {
        setItems(response.data);
      } else {
        setItems([]);
      }
    } catch (error) {
      toast.error('Failed to load finished goods report');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [selectedBranch]);

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = items.filter((item) => {
      const packedQty = item.packedQuantity ?? 0;
      // Skip rows with zero packed quantity
      if (packedQty <= 0) return false;

      if (!term) return true;
      const matchesEndgood = (item.endgoodName || '').toLowerCase().includes(term);
      const matchesCategory = (item.categoryName || '').toLowerCase().includes(term);
      return matchesEndgood || matchesCategory;
    });

    // We now focus on packed quantity only for this report
    const withDerived = filtered.map((item) => {
      const packedQty = item.packedQuantity ?? 0;
      const totalBoxes = item.totalBoxes ?? item.packedBoxes ?? 0;
      return {
        ...item,
        _packedQuantity: packedQty,
        _totalBoxes: totalBoxes,
      } as FinishedGoodsInventoryExtendedSummary & {
        _packedQuantity: number;
        _totalBoxes: number;
      };
    });

    return withDerived.sort(
      (a, b) => (b._packedQuantity || 0) - (a._packedQuantity || 0)
    );
  }, [items, searchTerm]);

  const handleClearFilters = () => {
    setSearchTerm('');
  };

  const handleExport = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const headers = ['Endgood Name', 'Category Name', 'Packed Quantity', 'Total Boxes'];
      const csvRows = [
        headers.join(','),
        ...filteredData.map((item) => {
          const packedQty =
            (item as any)._packedQuantity ??
            item.packedQuantity ??
            0;
          const totalBoxes =
            (item as any)._totalBoxes ??
            item.totalBoxes ??
            item.packedBoxes ??
            0;
          const row = [
            `"${(item.endgoodName || '').replace(/"/g, '""')}"`,
            `"${(item.categoryName || '').replace(/"/g, '""')}"`,
            packedQty.toString(),
            totalBoxes.toString()
          ];
          return row.join(',');
        })
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'finished-goods-inventory-report.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filteredData.length} records to CSV`);
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2">Loading Finished Goods Report...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <Package className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Finished Goods Inventory Report</CardTitle>
                  <CardDescription className="text-primary-100">
                    Current finished goods stock summary
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/reports')}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Reports
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchReport}
                  disabled={loading}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={filteredData.length === 0}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="text-lg text-primary-800">Search & Filters</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="searchTerm">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="searchTerm"
                    placeholder="Search endgood or category..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={!searchTerm}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="text-lg text-primary-800">Finished Goods Inventory</CardTitle>
            <CardDescription className="text-gray-600">
              Showing {filteredData.length} records
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Endgood Name</TableHead>
                  <TableHead>Category Name</TableHead>
                  <TableHead className="text-right">Packed Quantity</TableHead>
                  <TableHead className="text-right">Total Boxes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <TableRow key={`${item.endgoodName}-${index}`}>
                      <TableCell className="font-medium">{item.endgoodName}</TableCell>
                      <TableCell>{item.categoryName || '-'}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(
                          (item as any)._packedQuantity ??
                            item.packedQuantity ??
                            0
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(
                          (item as any)._totalBoxes ??
                            item.totalBoxes ??
                            item.packedBoxes ??
                            0
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FinishedGoodsInventoryReportPage;
